/**
 * Cliente HTTP de Softfi.
 *
 * Todas las llamadas al backend pasan por aquí. Se encarga de tres cosas que si
 * no estuvieran centralizadas habría que repetir en cada pantalla:
 *
 *   1. Poner el token de sesión en cada petición.
 *   2. RENOVAR la sesión sola cuando el token expira, y reintentar la llamada.
 *      El usuario no se entera de nada: no lo saca al login cada hora.
 *   3. Traducir los errores del backend a un objeto de error uniforme.
 */

import type { ApiError, Session } from "./types";

/** El código del error —y el nombre del evento— de una cuenta suspendida por falta de pago. */
export const SUSCRIPCION_VENCIDA = "subscription_expired";

const BASE = "/api/v1";

const LLAVE_ACCESO = "softfi-access-token";
const LLAVE_REFRESCO = "softfi-refresh-token";

// -----------------------------------------------------------------------------
//  Guardado de los tokens
// -----------------------------------------------------------------------------
//
// Se guardan en localStorage para que la sesión sobreviva al cerrar el navegador
// — imprescindible en un POS: el cajero no puede estar iniciando sesión cada vez
// que el celular se bloquea.
//
// (Nota de seguridad: lo más seguro serían cookies httpOnly, pero el POS tiene
// que funcionar sin conexión leyendo su token del dispositivo. Se compensa con
// tokens de acceso cortos, rotación de refresh y detección de reutilización, que
// ya están implementados en el backend.)

export const tokens = {
  acceso(): string | null {
    return safeGet(LLAVE_ACCESO);
  },
  refresco(): string | null {
    return safeGet(LLAVE_REFRESCO);
  },
  guardar(acceso: string, refresco: string) {
    safeSet(LLAVE_ACCESO, acceso);
    safeSet(LLAVE_REFRESCO, refresco);
  },
  borrar() {
    safeRemove(LLAVE_ACCESO);
    safeRemove(LLAVE_REFRESCO);
  },
  hay(): boolean {
    return Boolean(safeGet(LLAVE_ACCESO));
  },
};

function safeGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function safeSet(k: string, v: string) {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* modo privado del navegador: seguimos en memoria */
  }
}
function safeRemove(k: string) {
  try {
    localStorage.removeItem(k);
  } catch {
    /* nada */
  }
}

// -----------------------------------------------------------------------------
//  Error tipado
// -----------------------------------------------------------------------------

export class SoftfiError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;

  constructor(mensaje: string, code: string, status: number, details?: Record<string, unknown>) {
    super(mensaje);
    this.name = "SoftfiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** Errores por campo, para pintarlos debajo del input correspondiente. */
  get errorsPorCampo(): Record<string, string> {
    const campos = (this.details?.fields as { field: string; message: string }[]) ?? [];
    return Object.fromEntries(campos.map((c) => [c.field, c.message]));
  }
}

// -----------------------------------------------------------------------------
//  Qué hacer cuando la sesión ya no se puede salvar
// -----------------------------------------------------------------------------

let alPerderSesion: (() => void) | null = null;

/** El store de autenticación registra aquí su función de cierre de sesión. */
export function registrarCierreDeSesion(fn: () => void) {
  alPerderSesion = fn;
}

// -----------------------------------------------------------------------------
//  La huella del acceso, en TODAS las respuestas (2026-10-19)
// -----------------------------------------------------------------------------
//
// El servidor pone en cada respuesta con sesión `X-Softfi-Acceso`: una huella
// de lo que puede hacer quien pide (rol, permisos, sucursales, plan). Si la
// huella cambia, alguien le dio o le quitó algo mientras trabajaba, y la
// pantalla se pone al día sin cerrar sesión. La registra el store de
// autenticación (`actualizarAcceso`). Ver `huella_de_acceso` en el servidor.

let alVerHuellaDeAcceso: ((huella: string) => void) | null = null;

export function registrarHuellaDeAcceso(fn: (huella: string) => void) {
  alVerHuellaDeAcceso = fn;
}

// -----------------------------------------------------------------------------
//  La sucursal de este aparato, en TODAS las peticiones
// -----------------------------------------------------------------------------
//
// Desde el 2026-09-27 cada sucursal tiene su catálogo. La tablet de la Norte
// tiene que ver el de la Norte en Catálogo, Precios, el lector, importar… sin
// que cada pantalla se acuerde de decirlo: una sola que se olvidara enseñaría
// el catálogo de la Principal, con sus precios, sin ningún error.
//
// Por eso va aquí, en la cabecera `X-Softfi-Sucursal`, y el servidor la usa
// cuando la petición no dice otra (`ctx.sucursal_del_aparato`). La registra el
// store de autenticación, que es quien sabe de qué negocio es.

let sucursalDelAparato: (() => string | null) | null = null;

export function registrarSucursalDelAparato(fn: () => string | null) {
  sucursalDelAparato = fn;
}

// -----------------------------------------------------------------------------
//  ⚠️ EL NEGOCIO DE LA SESIÓN Y EL DE LA PANTALLA TIENEN QUE SER EL MISMO
// -----------------------------------------------------------------------------
//
// Lo vio el dueño (2026-09-29): arriba decía «Tiendas Moondo» y la Caja era la
// de Total Venezuela, «abierta por Angelo». La sesión (los tokens) se guarda
// en el aparato y la COMPARTEN todas las pestañas de Softfi; el nombre del
// negocio de arriba lo lleva cada pestaña por su cuenta. Así que cambiar de
// negocio en una pestaña dejaba a la otra pidiendo los datos del negocio nuevo
// con el nombre del viejo — sin ningún error, que es lo peligroso.
//
// Ahora, antes de cada petición, se compara el negocio del token (`tid`) con
// el de la pantalla. Si no coinciden, la pestaña se pone al día con la sesión
// (`alDescuadrarNegocio`) y la petición no sale: mejor un instante cargando que
// una cifra de otro negocio con el nombre de este.

let negocioEsperado: (() => string | null) | null = null;
let alDescuadrarNegocio: (() => void) | null = null;

export function registrarNegocioEsperado(esperado: () => string | null, alDescuadre: () => void) {
  negocioEsperado = esperado;
  alDescuadrarNegocio = alDescuadre;
}

/** El negocio del token de acceso (`tid`), o `null` si no se puede leer. */
export function negocioDelToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const cuerpo = token.split(".")[1];
    if (!cuerpo) return null;
    const base64 = cuerpo.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))) as {
      tid?: unknown;
    };
    return typeof json.tid === "string" ? json.tid : null;
  } catch {
    return null;
  }
}

function descuadrado(acceso: string | null): boolean {
  const esperado = negocioEsperado?.();
  const delToken = negocioDelToken(acceso);
  return Boolean(esperado && delToken && esperado !== delToken);
}

// Y si OTRA pestaña cambia de negocio, esta se entera al momento, sin esperar a
// su próxima petición: el evento `storage` solo llega a las demás pestañas.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === LLAVE_ACCESO && e.newValue && descuadrado(e.newValue)) {
      alDescuadrarNegocio?.();
    }
  });
}

// -----------------------------------------------------------------------------
//  Renovación de sesión (una sola a la vez)
// -----------------------------------------------------------------------------
//
// Si tres peticiones fallan al mismo tiempo por token expirado, NO queremos tres
// renovaciones simultáneas: la primera rotaría el token y las otras dos llegarían
// con un token ya usado, que el backend interpreta como robo y cierra todo.
//
// Por eso se guarda la promesa en curso y las demás esperan a la misma.

let renovacionEnCurso: Promise<boolean> | null = null;

async function renovarSesion(): Promise<boolean> {
  if (renovacionEnCurso) return renovacionEnCurso;

  renovacionEnCurso = (async () => {
    const refresco = tokens.refresco();
    if (!refresco) return false;

    try {
      const respuesta = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresco }),
      });

      if (!respuesta.ok) return false;

      const sesion = (await respuesta.json()) as Session;
      // ⚠️ Si mientras se renovaba la sesión cambió —se cambió de negocio, o
      // se entró de nuevo—, lo nuevo manda: guardar esto la pisaría con la del
      // negocio de antes. Se descarta y se sigue con la actual.
      if (tokens.refresco() !== refresco) return true;
      tokens.guardar(sesion.access_token, sesion.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      // Se libera en el siguiente ciclo para que las peticiones que estaban
      // esperando alcancen a leer el resultado.
      setTimeout(() => {
        renovacionEnCurso = null;
      }, 0);
    }
  })();

  return renovacionEnCurso;
}

// -----------------------------------------------------------------------------
//  La función principal
// -----------------------------------------------------------------------------

type Opciones = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  /** true en login y registro: no llevan token ni intentan renovar sesión. */
  publico?: boolean;
  /** Para cortar una petición que se quedó esperando (el teléfono de la PC). */
  signal?: AbortSignal;
  /** Cabeceras de más: la llave del teléfono (`features/telefono`). */
  cabeceras?: Record<string, string>;
};

export async function api<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const { method = "GET", body, params, publico = false, signal, cabeceras: extra } = opciones;

  const url = new URL(`${BASE}${ruta}`, window.location.origin);
  if (params) {
    for (const [clave, valor] of Object.entries(params)) {
      if (valor !== undefined && valor !== null && valor !== "") {
        url.searchParams.set(clave, String(valor));
      }
    }
  }

  // ⚠️ Un archivo NO se manda como JSON, y el `Content-Type` hay que DEJARLO
  // en blanco a propósito.
  //
  // Al subir un archivo, el navegador tiene que poner
  // `multipart/form-data; boundary=----X`, donde ese «boundary» es un
  // separador aleatorio que él mismo inventa. Si lo escribimos nosotros, va sin
  // separador y el servidor no encuentra dónde empieza el archivo: contesta un
  // 422 hablando de un campo que sí mandamos. Es de los errores más
  // desconcertantes que hay.
  const esArchivo = body instanceof FormData;

  const ejecutar = async (): Promise<Response> => {
    const cabeceras: Record<string, string> = { Accept: "application/json", ...extra };
    if (body !== undefined && !esArchivo) cabeceras["Content-Type"] = "application/json";

    const acceso = tokens.acceso();
    if (!publico && acceso) cabeceras["Authorization"] = `Bearer ${acceso}`;

    const sucursal = publico ? null : sucursalDelAparato?.();
    if (sucursal) cabeceras["X-Softfi-Sucursal"] = sucursal;

    return fetch(url.toString(), {
      method,
      headers: cabeceras,
      body: body === undefined ? undefined : esArchivo ? (body as FormData) : JSON.stringify(body),
      signal,
    });
  };

  // ⚠️ La sesión, del mismo negocio que la pantalla. Ver `negocioEsperado`.
  // `/auth/…` queda fuera: es justo con lo que la pestaña se pone al día.
  if (!publico && !ruta.startsWith("/auth/") && descuadrado(tokens.acceso())) {
    alDescuadrarNegocio?.();
    throw new SoftfiError("Se está actualizando el negocio de esta ventana.", "negocio_cambiado", 409);
  }

  let respuesta: Response;
  try {
    respuesta = await ejecutar();
  } catch {
    // No hubo respuesta del servidor: está apagado o no hay internet.
    throw new SoftfiError(
      "No se pudo conectar con el servidor. Revisa tu conexión a internet.",
      "network_error",
      0,
    );
  }

  // --- Token expirado: renovamos y reintentamos UNA vez ---
  if (respuesta.status === 401 && !publico) {
    const renovado = await renovarSesion();
    if (renovado) {
      respuesta = await ejecutar();
    } else {
      tokens.borrar();
      alPerderSesion?.();
      throw new SoftfiError("Tu sesión expiró. Inicia sesión de nuevo.", "session_expired", 401);
    }
  }

  // También en los errores: un 403 por un permiso recién quitado es justo el
  // momento de enterarse.
  const huella = respuesta.headers.get("X-Softfi-Acceso");
  if (huella && !publico) alVerHuellaDeAcceso?.(huella);

  if (respuesta.status === 204) return undefined as T;

  const texto = await respuesta.text();
  const datos = texto ? safeJson(texto) : null;

  if (!respuesta.ok) {
    const error = (datos as ApiError | null)?.error;
    // La cuenta quedó suspendida mientras se usaba: la app enseña en ese momento
    // la pantalla de «Suscripción vencida» (ver `AppLayout`), en vez de dejar
    // un error suelto en cada botón.
    if (error?.code === SUSCRIPCION_VENCIDA) window.dispatchEvent(new Event(SUSCRIPCION_VENCIDA));
    throw new SoftfiError(
      error?.message ?? `Error ${respuesta.status}. Intenta de nuevo.`,
      error?.code ?? "unknown_error",
      respuesta.status,
      error?.details,
    );
  }

  return datos as T;
}

/**
 * Baja un archivo del backend (Excel, PDF…) y lo guarda en el dispositivo.
 *
 * No se puede usar `api()` para esto: `api()` lee la respuesta como texto y la
 * pasa por `JSON.parse`, que con un .xlsx devuelve basura.
 *
 * Tampoco vale un `<a href>` normal, y esa es la razón de que exista esta
 * función: un enlace lo abre el navegador **sin la cabecera Authorization**, así
 * que el backend respondería 401 y el usuario vería una pantalla en blanco. Hay
 * que pedirlo con fetch, token incluido, y después crear el enlace en memoria.
 */
export async function descargar(ruta: string, opciones: Opciones = {}): Promise<void> {
  const { params } = opciones;

  const url = new URL(`${BASE}${ruta}`, window.location.origin);
  if (params) {
    for (const [clave, valor] of Object.entries(params)) {
      if (valor !== undefined && valor !== null && valor !== "") {
        url.searchParams.set(clave, String(valor));
      }
    }
  }

  // Un Excel de otro negocio con el nombre de este: lo mismo que en `api`.
  if (descuadrado(tokens.acceso())) {
    alDescuadrarNegocio?.();
    throw new SoftfiError("Se está actualizando el negocio de esta ventana.", "negocio_cambiado", 409);
  }

  const pedir = () => {
    const cabeceras: Record<string, string> = {};
    if (tokens.acceso()) cabeceras["Authorization"] = `Bearer ${tokens.acceso()}`;
    const sucursal = sucursalDelAparato?.();
    if (sucursal) cabeceras["X-Softfi-Sucursal"] = sucursal;
    return fetch(url.toString(), { headers: cabeceras });
  };

  let respuesta: Response;
  try {
    respuesta = await pedir();
  } catch {
    throw new SoftfiError(
      "No se pudo conectar con el servidor. Revisa tu conexión a internet.",
      "network_error",
      0,
    );
  }

  if (respuesta.status === 401 && (await renovarSesion())) {
    respuesta = await pedir();
  }

  // Un error SÍ viene en JSON (402 de plan, 403 de permiso…), así que se lee
  // como texto para poder enseñar el mensaje real y no un "error 402" pelado.
  if (!respuesta.ok) {
    const datos = safeJson(await respuesta.text()) as ApiError | null;
    throw new SoftfiError(
      datos?.error?.message ?? `Error ${respuesta.status}. Intenta de nuevo.`,
      datos?.error?.code ?? "unknown_error",
      respuesta.status,
      datos?.error?.details,
    );
  }

  // El nombre lo manda el backend en Content-Disposition; si no llegara, uno
  // genérico es mejor que un archivo llamado "descarga" sin extensión.
  const disposicion = respuesta.headers.get("Content-Disposition") ?? "";
  const nombre = /filename="?([^";]+)"?/.exec(disposicion)?.[1] ?? "softfi.xlsx";

  const blob = await respuesta.blob();
  const enlaceTemporal = document.createElement("a");
  enlaceTemporal.href = URL.createObjectURL(blob);
  enlaceTemporal.download = nombre;
  document.body.appendChild(enlaceTemporal);
  enlaceTemporal.click();
  document.body.removeChild(enlaceTemporal);

  // Sin esto el archivo se queda en memoria hasta recargar la página. Con
  // respaldos de varios megas, bajar tres seguidos se nota.
  setTimeout(() => URL.revokeObjectURL(enlaceTemporal.href), 1000);
}

function safeJson(texto: string): unknown {
  try {
    return JSON.parse(texto);
  } catch {
    return null;
  }
}

/** Mensaje legible para cualquier error, venga de donde venga. */
export function mensajeDeError(error: unknown): string {
  if (error instanceof SoftfiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Ocurrió un error inesperado.";
}
